import { FormMode } from '../types';
import { isValidEmail, isValidLoginIdentifier, isValidLoginUsername, isValidPassword, isValidRegisterUsername } from './passwordCredentials';
export const DataEntry = {
    DISABLED: 'DISABLED',
    OPTIONAL: 'OPTIONAL',
    OTHER: 'OTHER',
    REQUIRED: 'REQUIRED'
};
const KNOWN_ENTRIES = new Set(Object.values(DataEntry));
const ENTRY_KEYS = [
    'loginEmailEntry',
    'loginEmailVerify',
    'loginPasswordEntry',
    'loginUsernameEntry',
    'signupEmailEntry',
    'signupEmailVerify',
    'signupPasswordEntry',
    'signupUsernameEntry'
];
export const isEnabled = (value) => value === DataEntry.OPTIONAL || value === DataEntry.REQUIRED;
export const isRequired = (value) => value === DataEntry.REQUIRED;
/** OTP verify: OPTIONAL is treated as disabled until BE productizes it. */
export const isVerifyEnabled = (value) => value === DataEntry.REQUIRED;
export const hasAuthEntryFields = (data) => ENTRY_KEYS.some((key) => data[key] != null);
const emptyTab = (overrides) => ({
    requiresEmail: false,
    requiresEmailVerify: false,
    requiresPassword: false,
    requiresUsername: false,
    showEmail: false,
    showForgotPassword: false,
    showIdentifier: false,
    showPassword: false,
    showUsername: false,
    usesDirectAuth: false,
    ...overrides
});
export const legacyAuthPolicy = (pwdRequired) => {
    const otpTab = emptyTab({
        requiresEmail: true,
        requiresEmailVerify: true,
        requiresPassword: pwdRequired,
        showEmail: true,
        showPassword: pwdRequired
    });
    return { fromPreAuth: false, login: otpTab, signup: otpTab };
};
const tabFromEntries = (emailEntry, usernameEntry, passwordEntry, verifyEntry, isLogin) => {
    const showEmail = isEnabled(emailEntry);
    const showUsername = isEnabled(usernameEntry);
    const showPassword = isEnabled(passwordEntry);
    const requiresEmailVerify = isVerifyEnabled(verifyEntry);
    const bothRequired = isRequired(emailEntry) && isRequired(usernameEntry);
    return emptyTab({
        requiresEmail: isRequired(emailEntry),
        requiresEmailVerify,
        requiresPassword: isRequired(passwordEntry),
        requiresUsername: isRequired(usernameEntry),
        showEmail,
        showForgotPassword: isLogin && showEmail && showPassword,
        showIdentifier: showEmail && showUsername && !bothRequired,
        showPassword,
        showUsername,
        usesDirectAuth: !requiresEmailVerify
    });
};
/** Combined username-or-email field when both identifiers are enabled. */
export const usesCombinedIdentifier = (tab) => tab.showEmail && tab.showUsername;
/** Login identifier field when username is on, including username-only (no email). */
export const usesLoginIdentifier = (tab) => tab.showIdentifier || (tab.showUsername && !tab.showEmail);
/** PreLogin first, then password / missing identifier — when username is enabled. */
export const usesIdentifierFirst = (tab) => tab.showUsername;
/** Forgot Password on every login step when the brand has passwords, including the identifier step. */
export const shouldShowForgotPasswordLink = (tab, isLogin, choosePassword = false) => isLogin && !choosePassword && tab.showPassword;
/** Login after OTP set/reset must send ActionType=PASSWORD_RESET, not a normal login. */
export const isPasswordResetLogin = (choosePassword, actionType) => choosePassword || actionType === 'PASSWORD_RESET';
export const policyFromPreAuth = (data, warn = console.warn) => {
    if (!hasAuthEntryFields(data)) {
        return legacyAuthPolicy(Boolean(data.pwdRequired));
    }
    for (const key of ENTRY_KEYS) {
        const value = data[key];
        if (value == null) {
            warn(`[auth] PreAuth.${key} missing; treating as DISABLED`);
            continue;
        }
        if (!KNOWN_ENTRIES.has(value)) {
            warn(`[auth] PreAuth.${key} unknown value ${value}; treating as DISABLED`);
        }
    }
    return {
        fromPreAuth: true,
        login: tabFromEntries(data.loginEmailEntry, data.loginUsernameEntry, data.loginPasswordEntry, data.loginEmailVerify, true),
        signup: tabFromEntries(data.signupEmailEntry, data.signupUsernameEntry, data.signupPasswordEntry, data.signupEmailVerify, false)
    };
};
export const tabPolicy = (policy, mode) => mode === FormMode.Register ? policy.signup : policy.login;
const firstIdentifierReady = (tab, mode, fields) => {
    if (usesCombinedIdentifier(tab) || usesLoginIdentifier(tab)) {
        return isValidLoginIdentifier(fields.email) || isValidLoginUsername(fields.username);
    }
    if (tab.showUsername && !tab.showEmail) {
        return mode === FormMode.Register
            ? isValidRegisterUsername(fields.username)
            : isValidLoginUsername(fields.username);
    }
    if (tab.showEmail)
        return isValidEmail(fields.email);
    return false;
};
export const isDirectAuthReady = (tab, mode, fields, minPasswordLength) => {
    if (fields.needSecondIdentifier) {
        if (tab.showEmail && !isValidEmail(fields.email))
            return false;
        if (tab.showUsername && !isValidRegisterUsername(fields.username))
            return false;
        return true;
    }
    /** Register always shows every enabled field on one screen; identifier-first is a Login-only pattern (unknown password). */
    if (mode === FormMode.Login && usesIdentifierFirst(tab) && !fields.identifierResolved) {
        return firstIdentifierReady(tab, mode, fields);
    }
    if (fields.choosePassword) {
        // The reset can be identified by email or by username - only email goes through OTP
        // verification, so emailVerified alone can't gate a username-identified reset.
        if (!fields.emailVerified)
            return false;
        if (!isValidEmail(fields.email) && !isValidLoginUsername(fields.username))
            return false;
        if (!fields.passwordSet ||
            !fields.password ||
            !isValidPassword(fields.password, minPasswordLength)) {
            return false;
        }
        return fields.passwordConfirmSet && fields.passwordConfirm === fields.password;
    }
    if (mode === FormMode.Login) {
        const identifierOk = usesLoginIdentifier(tab)
            ? isValidLoginIdentifier(fields.email) || isValidLoginUsername(fields.username)
            : tab.showEmail
                ? isValidEmail(fields.email)
                : tab.showUsername
                    ? isValidLoginUsername(fields.username)
                    : false;
        if (!identifierOk)
            return false;
        if (tab.requiresPassword && !fields.password)
            return false;
        return true;
    }
    if (tab.showEmail) {
        const emailRequired = tab.requiresEmail || !tab.showUsername;
        if (emailRequired && !isValidEmail(fields.email))
            return false;
        if (!emailRequired && fields.email && !isValidEmail(fields.email))
            return false;
    }
    if (tab.requiresUsername && !isValidRegisterUsername(fields.username))
        return false;
    if (tab.showUsername && fields.username && !isValidRegisterUsername(fields.username))
        return false;
    if (tab.requiresPassword) {
        if (!fields.passwordSet ||
            !fields.password ||
            !isValidPassword(fields.password, minPasswordLength)) {
            return false;
        }
        if (!fields.passwordConfirmSet || fields.passwordConfirm !== fields.password)
            return false;
    }
    return true;
};
export const isAvailableStatus = (status) => status === 'AVAILABLE';
export const isConflictStatus = (status) => status === 'RESOLVE_FAILED' || status === 'CONFLICT';
export const isPasswordMissing = (prelogin) => prelogin.passwordStatus === 'MISSING' && !prelogin.pwdEnabled;
